import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { RenewalNudge } from '../components/RenewalNudge';
import { NotificationBell } from '../components/NotificationBell';
import { NotificationPreferences } from '../components/NotificationPreferences';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger
} from '../components/ui/sheet';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  User, Globe, Bell, ClipboardList, Bookmark, CreditCard,
  ShieldCheck, FileText, LogOut, Settings, Star, Heart, ChevronLeft, ChevronRight,
  Calendar, Sparkles, NotebookPen
} from 'lucide-react';
import { PatientNotesView } from '../components/PatientNotesView';
import { MedicalFilesPanel } from '../components/MedicalFilesPanel';
import { ProfilePictureUploader } from '../components/ProfilePictureUploader';
import { CallBalanceBadge } from '../components/CallBalanceBadge';
import { getLegalDocUrl, LEGAL_LABELS } from '../lib/legalDocs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Reusable list row matching the reference (vertical menu)
const MenuRow = ({ icon: Icon, label, onClick, danger, testId, rightSlot, isRTL, Chevron, iconBg }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`group w-full bg-white rounded-2xl shadow-sm border border-gray-100 px-4 py-4 min-h-[60px] flex items-center gap-3 transition-all hover:shadow-md hover:-translate-y-px hover:border-blue-200
      ${isRTL ? 'flex-row-reverse text-right' : 'text-left'}`}
  >
    <span
      className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-transform group-hover:scale-105
        ${danger ? 'bg-red-50 text-red-500' : iconBg || 'bg-blue-50 text-[#1565c0]'}`}
    >
      <Icon size={20} strokeWidth={1.9} />
    </span>
    <span className={`flex-1 font-bold text-base ${danger ? 'text-red-500' : 'text-[#0d47a1]'}`}>
      {label}
    </span>
    {rightSlot}
    <Chevron size={18} className={`${danger ? 'text-red-400' : 'text-gray-400 group-hover:text-[#1565c0]'} transition-colors`} />
  </button>
);

const StatCard = ({ icon: Icon, title, onClick, testId, accent, gradient }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`relative overflow-hidden rounded-2xl px-4 py-4 sm:px-3 sm:py-5 flex items-center sm:flex-col sm:items-center sm:justify-center gap-3 sm:gap-2 min-h-[68px] sm:min-h-[124px] shadow-sm border border-white/40 hover:shadow-xl sm:hover:-translate-y-1 transition-all
      ${gradient || 'bg-gradient-to-br from-white to-blue-50'}`}
  >
    <span className="pointer-events-none absolute -top-6 -right-6 w-20 h-20 rounded-full bg-white/40 blur-xl" aria-hidden="true" />
    <div className="relative w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-white/90 shadow-inner flex items-center justify-center flex-shrink-0">
      <Icon size={22} className="text-[#1565c0]" strokeWidth={2} />
    </div>
    <span className="relative font-extrabold text-[#0d47a1] text-base sm:text-sm md:text-base text-start sm:text-center leading-tight flex-1 sm:flex-none">
      {title}
    </span>
    {accent && (
      <span className="relative text-[10px] font-bold text-[#1565c0]/80 uppercase tracking-[0.18em]">
        {accent}
      </span>
    )}
  </button>
);

export const PatientDashboard = () => {
  const { user, logout, checkAuth } = useAuth();
  const { t, language, setLanguage, labels } = useLanguage();
  const navigate = useNavigate();

  const [appointments, setAppointments] = useState([]);
  const [subscription, setSubscription] = useState(null);

  // Sheets / Dialogs
  const [consultationsOpen, setConsultationsOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [savedOpen, setSavedOpen] = useState(false);
  const [paymentsOpen, setPaymentsOpen] = useState(false);

  const [profileForm, setProfileForm] = useState({
    full_name: '',
    phone_number: '',
    email: '',
    age: '',
    gender: '',
    location: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);

  const isRTL = language === 'ar';
  const Chevron = isRTL ? ChevronLeft : ChevronRight;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [appointmentsRes, subRes] = await Promise.all([
          axios.get(`${API}/appointments`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API}/subscriptions/my-subscription`, { withCredentials: true }).catch(() => ({ data: { active: false } }))
        ]);
        setAppointments(appointmentsRes.data || []);
        if (subRes.data?.active) setSubscription(subRes.data.subscription);
      } catch (e) {
        // ignore
      }
    };
    fetchData();
  }, []);

  const upcoming = appointments.filter(a => a.status !== 'completed');
  const past = appointments.filter(a => a.status === 'completed');

  const openEditProfile = () => {
    setProfileForm({
      full_name: user?.full_name || '',
      phone_number: user?.phone_number || '',
      email: user?.email || '',
      age: user?.age != null ? String(user.age) : '',
      gender: user?.gender || '',
      location: user?.location || ''
    });
    setEditProfileOpen(true);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const payload = { ...profileForm };
      if (payload.age === '' || payload.age == null) {
        delete payload.age;
      } else {
        payload.age = parseInt(payload.age, 10);
      }
      if (!payload.gender) delete payload.gender;
      if (!payload.email) delete payload.email;
      await axios.put(`${API}/users/profile`, payload, { withCredentials: true });
      if (typeof checkAuth === 'function') await checkAuth();
      toast.success(t('تم تحديث الملف الشخصي', 'Profile updated', 'Profil mis à jour'));
      setEditProfileOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل التحديث', 'Update failed', 'Échec de la mise à jour'));
    } finally {
      setSavingProfile(false);
    }
  };

  const openLegalPdf = (type) => {
    const url = getLegalDocUrl(type, language);
    if (url) navigate(url);
  };

  return (
    <div className={`min-h-screen bg-[#f4f7fb] ${isRTL ? 'rtl' : 'ltr'}`} data-testid="patient-dashboard">

      {/* Blue header */}
      <div className="relative">
        <div className="bg-gradient-to-l from-[#1565c0] to-[#0d47a1] pt-5 pb-16 px-4 relative">
          <div className="max-w-3xl mx-auto flex items-center justify-between">
            <button
              onClick={openEditProfile}
              className="w-11 h-11 bg-white/95 rounded-full flex items-center justify-center text-[#1565c0] shadow"
              data-testid="header-profile-icon"
              aria-label="profile"
            >
              <User size={20} />
            </button>

            <div className="bg-white/95 rounded-full shadow" data-testid="header-notif-wrapper">
              <NotificationBell />
            </div>

            <div className="flex items-center gap-2 text-white">
              <img src="/images/logo.png" alt="Tababa" className="h-8 w-8 rounded-full bg-white p-0.5 shadow" />
              <span className="text-2xl font-extrabold" style={{ fontFamily: '"Tajawal","Cairo",sans-serif' }}>
                {t('طبابة', 'Tababa', 'Tababa')}
              </span>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-1 bg-white/95 rounded-full px-3 h-11 text-[#1565c0] text-sm font-semibold shadow"
                  data-testid="header-language"
                >
                  <Globe size={16} />
                  <span>{labels[language]}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setLanguage('ar')}>العربية</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLanguage('en')}>English</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLanguage('fr')}>Français</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Curved bottom & little pill notch */}
        <svg className="absolute bottom-[-1px] left-0 w-full" viewBox="0 0 1440 70" preserveAspectRatio="none" aria-hidden="true">
          <path d="M0,0 C480,90 960,90 1440,0 L1440,70 L0,70 Z" fill="#f4f7fb" />
        </svg>
        <span className="absolute left-1/2 -translate-x-1/2 bottom-3 w-12 h-1 rounded-full bg-white/60" />
      </div>

      {/* Profile block */}
      <div className="max-w-3xl mx-auto px-4 -mt-6 sm:-mt-14 relative z-10">
        <RenewalNudge />
        {/* Hero card: avatar overlapping the curved header */}
        <div
          className="bg-white rounded-3xl shadow-lg border border-blue-50 px-5 pt-16 pb-6 mb-6 relative"
          data-testid="profile-hero-card"
        >
          {/* Avatar centered, half-overlapping the top of the card */}
          <div className="absolute -top-12 left-1/2 -translate-x-1/2">
            <div
              className="w-24 h-24 rounded-full bg-gradient-to-br from-[#1565c0] to-[#22c8b8] p-[3px] shadow-xl"
              data-testid="profile-avatar"
            >
              <div className="w-full h-full rounded-full bg-white flex items-center justify-center overflow-hidden">
                {user?.profile_photo ? (
                  <img src={user.profile_photo} alt={user.full_name} className="w-full h-full object-cover rounded-full" />
                ) : (
                  <User size={50} className="text-[#1565c0]" strokeWidth={1.5} />
                )}
              </div>
            </div>
            {/* Tiny verified-style accent */}
            <span className="absolute bottom-0 end-0 bg-white rounded-full p-0.5 shadow">
              <span className="block w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
                <Heart size={11} className="text-white fill-white" />
              </span>
            </span>
          </div>

          <div className="text-center">
            <h1 className="text-xl md:text-2xl font-extrabold text-[#0d47a1] leading-snug" data-testid="profile-welcome">
              {t('مرحباً بك، ', 'Welcome, ', 'Bienvenue, ')}
              <span className="bg-gradient-to-l from-[#1565c0] to-[#22c8b8] bg-clip-text text-transparent">
                {user?.full_name || ''}
              </span>
            </h1>

            {/* Status / role pill */}
            <div className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              {t('حساب نشط', 'Active account', 'Compte actif')}
            </div>

            {(user?.age || user?.gender) && (
              <p className="mt-3 text-xs text-gray-500 flex items-center justify-center gap-2 flex-wrap" data-testid="profile-age-gender">
                {user?.age && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-50 text-[#1565c0] font-semibold">
                    {t('العمر', 'Age', 'Âge')}: {user.age}
                  </span>
                )}
                {user?.gender && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-50 text-[#1565c0] font-semibold">
                    {t('الجنس', 'Gender', 'Sexe')}:&nbsp;
                    {user.gender === 'male' ? t('ذكر', 'Male', 'Homme')
                      : user.gender === 'female' ? t('أنثى', 'Female', 'Femme')
                      : t('آخر', 'Other', 'Autre')}
                  </span>
                )}
              </p>
            )}
            <p className="mt-3 flex items-center justify-center gap-2 text-gray-500 text-sm">
              <Heart size={14} className="text-[#1565c0] fill-[#1565c0]" />
              {t('نتمنى لك يوماً صحياً', 'Wishing you a healthy day', 'Belle journée santé')}
            </p>
          </div>
        </div>

        {/* Remaining consultations */}
        <div className="mb-6 flex justify-center" data-testid="dashboard-call-balance">
          <CallBalanceBadge className="w-full sm:w-auto justify-center" />
        </div>

        {/* Three stat/action cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-3 mb-6" data-testid="profile-stat-cards">
          <StatCard
            icon={Settings}
            title={t('إعدادات البروفايل', 'Profile Settings', 'Paramètres')}
            onClick={openEditProfile}
            testId="card-profile-settings"
            gradient="bg-gradient-to-br from-blue-50 to-cyan-50"
          />
          <StatCard
            icon={ClipboardList}
            title={t('باقاتي واشتراكاتي', 'My Plans', 'Mes forfaits')}
            onClick={() => navigate('/subscriptions')}
            testId="card-subscriptions"
            gradient="bg-gradient-to-br from-indigo-50 to-blue-50"
          />
          <StatCard
            icon={Star}
            title={t('نقاطي', 'My Points', 'Mes points')}
            accent="Points"
            onClick={() => toast.info(t('نظام النقاط قريباً', 'Points system coming soon', 'Bientôt disponible'))}
            testId="card-points"
            gradient="bg-gradient-to-br from-amber-50 to-orange-50"
          />
        </div>

        {/* Section label */}
        <p className={`text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 px-1 ${isRTL ? 'text-right' : 'text-left'}`}>
          {t('الحساب والإعدادات', 'Account & Settings', 'Compte & Paramètres')}
        </p>

        {/* Menu list */}
        <div className="space-y-2 pb-10" data-testid="profile-menu-list">

          {/* Language */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <div>
                <MenuRow
                  icon={Globe}
                  label={t('اللغة', 'Language', 'Langue')}
                  testId="menu-language"
                  rightSlot={<span className="text-xs text-gray-500 me-1">{labels[language]}</span>}
                  isRTL={isRTL}
                  Chevron={Chevron}
                />
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align={isRTL ? 'start' : 'end'} className="w-40">
              <DropdownMenuItem onClick={() => setLanguage('ar')}>العربية</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setLanguage('en')}>English</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setLanguage('fr')}>Français</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Notifications */}
          <MenuRow
            icon={Bell}
            label={t('الإشعارات', 'Notifications', 'Notifications')}
            onClick={() => setNotifOpen(true)}
            testId="menu-notifications"
            isRTL={isRTL}
            Chevron={Chevron}
            iconBg="bg-amber-50 text-amber-600"
          />

          {/* Past consultations -> opens sheet with tabs */}
          <MenuRow
            icon={ClipboardList}
            label={t('الاستشارات السابقة', 'Past Consultations', 'Consultations passées')}
            onClick={() => setConsultationsOpen(true)}
            testId="menu-consultations"
            isRTL={isRTL}
            Chevron={Chevron}
            iconBg="bg-indigo-50 text-indigo-600"
          />

          {/* Saved */}
          <MenuRow
            icon={Bookmark}
            label={t('المحفوظات', 'Saved', 'Favoris')}
            onClick={() => setSavedOpen(true)}
            testId="menu-saved"
            isRTL={isRTL}
            Chevron={Chevron}
            iconBg="bg-purple-50 text-purple-600"
          />

          {/* Payment methods */}
          <MenuRow
            icon={CreditCard}
            label={t('طرق الدفع', 'Payment Methods', 'Moyens de paiement')}
            onClick={() => setPaymentsOpen(true)}
            testId="menu-payments"
            isRTL={isRTL}
            Chevron={Chevron}
            iconBg="bg-emerald-50 text-emerald-600"
          />

          {/* Privacy policy */}
          <MenuRow
            icon={ShieldCheck}
            label={LEGAL_LABELS.privacy[language] || LEGAL_LABELS.privacy.en}
            onClick={() => openLegalPdf('privacy')}
            testId="menu-privacy"
            isRTL={isRTL}
            Chevron={Chevron}
            iconBg="bg-teal-50 text-teal-600"
          />

          {/* Terms */}
          <MenuRow
            icon={FileText}
            label={LEGAL_LABELS.terms[language] || LEGAL_LABELS.terms.en}
            onClick={() => openLegalPdf('terms')}
            testId="menu-terms"
            isRTL={isRTL}
            Chevron={Chevron}
            iconBg="bg-slate-100 text-slate-600"
          />

          {/* Doctor notes & prescriptions — read-only summary of every note any provider wrote for this patient */}
          <MenuRow
            icon={NotebookPen}
            label={t('وصفاتي وملاحظاتي', 'My Notes & Prescriptions', 'Notes & ordonnances')}
            onClick={() => setNotesOpen(true)}
            testId="menu-my-notes"
            isRTL={isRTL}
            Chevron={Chevron}
            iconBg="bg-orange-50 text-orange-600"
          />

          {/* AI Assistant quick link */}
          <MenuRow
            icon={Sparkles}
            label={t('المساعد الذكي', 'AI Assistant', 'Assistant IA')}
            onClick={() => navigate('/ai-chat')}
            testId="menu-ai"
            isRTL={isRTL}
            Chevron={Chevron}
            iconBg="bg-violet-50 text-violet-600"
          />

          {/* Logout */}
          <MenuRow
            icon={LogOut}
            label={t('تسجيل الخروج', 'Logout', 'Déconnexion')}
            onClick={handleLogout}
            testId="menu-logout"
            danger
            isRTL={isRTL}
            Chevron={Chevron}
          />
        </div>
      </div>

      {/* Edit Profile Dialog */}
      <Dialog open={editProfileOpen} onOpenChange={setEditProfileOpen}>
        <DialogContent className={isRTL ? 'rtl' : 'ltr'} data-testid="edit-profile-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0d47a1]">
              {t('إعدادات البروفايل', 'Profile Settings', 'Paramètres du profil')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {/* Profile picture uploader — calls /api/users/me/profile-picture immediately */}
            <ProfilePictureUploader
              value={user?.profile_picture || user?.profile_photo}
              onChange={(url) => { if (typeof checkAuth === 'function') checkAuth(); }}
              mode="self"
              testId="patient-pic"
              size={84}
              className="justify-center"
            />
            <div>
              <Label>{t('الاسم الكامل', 'Full Name', 'Nom complet')}</Label>
              <Input
                value={profileForm.full_name}
                onChange={(e) => setProfileForm({ ...profileForm, full_name: e.target.value })}
                data-testid="edit-name-input"
                className="mt-1"
              />
            </div>
            <div>
              <Label>{t('رقم الهاتف', 'Phone', 'Téléphone')}</Label>
              <Input
                value={profileForm.phone_number}
                onChange={(e) => setProfileForm({ ...profileForm, phone_number: e.target.value })}
                data-testid="edit-phone-input"
                className="mt-1"
              />
            </div>
            <div>
              <Label>{t('البريد الإلكتروني', 'Email', 'E-mail')}</Label>
              <Input
                type="email"
                value={profileForm.email}
                onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                data-testid="edit-email-input"
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t('العمر', 'Age', 'Âge')}</Label>
                <Input
                  type="number"
                  min="16"
                  max="120"
                  value={profileForm.age}
                  onChange={(e) => setProfileForm({ ...profileForm, age: e.target.value })}
                  data-testid="edit-age-input"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>{t('الجنس', 'Gender', 'Sexe')}</Label>
                <Select
                  value={profileForm.gender}
                  onValueChange={(v) => setProfileForm({ ...profileForm, gender: v })}
                >
                  <SelectTrigger className="mt-1" data-testid="edit-gender-select">
                    <SelectValue placeholder={t('اختر', 'Select', 'Choisir')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">{t('ذكر', 'Male', 'Homme')}</SelectItem>
                    <SelectItem value="female">{t('أنثى', 'Female', 'Femme')}</SelectItem>
                    <SelectItem value="other">{t('آخر', 'Other', 'Autre')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>{t('الموقع', 'Location', 'Localisation')}</Label>
              <Input
                value={profileForm.location}
                onChange={(e) => setProfileForm({ ...profileForm, location: e.target.value })}
                data-testid="edit-location-input"
                className="mt-1"
              />
            </div>
            <Button
              onClick={handleSaveProfile}
              disabled={savingProfile}
              className="w-full bg-[#1565c0] hover:bg-[#0d47a1] h-11 rounded-xl font-bold"
              data-testid="save-profile-btn"
            >
              {savingProfile ? t('جاري الحفظ...', 'Saving...', 'Enregistrement...') : t('حفظ التغييرات', 'Save Changes', 'Enregistrer')}
            </Button>

            {/* Notification preferences */}
            <div className="mt-4">
              <NotificationPreferences />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Past Consultations Sheet */}
      <Sheet open={consultationsOpen} onOpenChange={setConsultationsOpen}>
        <SheetContent side={isRTL ? 'left' : 'right'} className={`${isRTL ? 'rtl' : 'ltr'} w-full sm:max-w-lg overflow-y-auto`} data-testid="consultations-sheet">
          <SheetHeader>
            <SheetTitle className="text-[#0d47a1]">
              {t('الاستشارات والمستندات', 'Consultations & Documents', 'Consultations & documents')}
            </SheetTitle>
          </SheetHeader>
          <Tabs defaultValue="upcoming" className="mt-4">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="upcoming" data-testid="tab-upcoming">{t('قادمة', 'Upcoming', 'À venir')}</TabsTrigger>
              <TabsTrigger value="past" data-testid="tab-past">{t('سابقة', 'Past', 'Passées')}</TabsTrigger>
              <TabsTrigger value="notes" data-testid="tab-notes">{t('وصفات', 'Rx', 'Rx')}</TabsTrigger>
              <TabsTrigger value="files" data-testid="tab-files">{t('ملفات', 'Files', 'Fichiers')}</TabsTrigger>
            </TabsList>

            <TabsContent value="upcoming" className="mt-4 space-y-3">
              {upcoming.length === 0 ? (
                <Card className="p-6 text-center">
                  <Calendar className="mx-auto text-gray-300 mb-2" size={36} />
                  <p className="text-gray-600 text-sm mb-3">{t('لا توجد مواعيد قادمة', 'No upcoming appointments', 'Aucun rendez-vous à venir')}</p>
                  <Link to="/" onClick={() => setConsultationsOpen(false)}>
                    <Button className="bg-[#1565c0] hover:bg-[#0d47a1]">
                      {t('احجز الآن', 'Book Now', 'Réserver')}
                    </Button>
                  </Link>
                </Card>
              ) : (
                upcoming.map((apt) => (
                  <Card key={apt._id} className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-bold text-base text-[#0d47a1]">{apt.doctor_name}</h3>
                        <p className="text-gray-600 text-xs">{apt.doctor_specialty}</p>
                        <p className="text-gray-600 text-sm">{apt.appointment_date} - {apt.appointment_time}</p>
                      </div>
                      <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-800">
                        {apt.status}
                      </span>
                    </div>
                  </Card>
                ))
              )}
            </TabsContent>

            <TabsContent value="past" className="mt-4 space-y-3">
              {past.length === 0 ? (
                <Card className="p-6 text-center text-gray-500 text-sm">
                  {t('لا يوجد سجل', 'No history', 'Aucun historique')}
                </Card>
              ) : (
                past.map((apt) => (
                  <Card key={apt._id} className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-bold text-base text-[#0d47a1]">{apt.doctor_name}</h3>
                        <p className="text-gray-600 text-sm">{apt.appointment_date}</p>
                      </div>
                      <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-800">
                        {apt.status}
                      </span>
                    </div>
                  </Card>
                ))
              )}
            </TabsContent>

            <TabsContent value="notes" className="mt-4">
              <PatientNotesView />
            </TabsContent>

            <TabsContent value="files" className="mt-4">
              <MedicalFilesPanel />
            </TabsContent>
          </Tabs>
        </SheetContent>
      </Sheet>

      {/* Notifications placeholder */}
      <Sheet open={notesOpen} onOpenChange={setNotesOpen}>
        <SheetContent side={isRTL ? 'left' : 'right'} className={`${isRTL ? 'rtl' : 'ltr'} w-full sm:max-w-lg overflow-y-auto`} data-testid="my-notes-sheet">
          <SheetHeader>
            <SheetTitle className="text-[#0d47a1]">
              {t('وصفاتي وملاحظاتي الطبية', 'My Notes & Prescriptions', 'Mes notes & ordonnances')}
            </SheetTitle>
          </SheetHeader>
          <div className="mt-4">
            <PatientNotesView />
          </div>
        </SheetContent>
      </Sheet>

      {/* Notifications placeholder */}
      <Sheet open={notifOpen} onOpenChange={setNotifOpen}>
        <SheetContent side={isRTL ? 'left' : 'right'} className={isRTL ? 'rtl' : 'ltr'}>
          <SheetHeader><SheetTitle className="text-[#0d47a1]">{t('الإشعارات', 'Notifications', 'Notifications')}</SheetTitle></SheetHeader>
          <div className="py-10 text-center text-gray-500 text-sm">
            <Bell className="mx-auto text-gray-300 mb-2" size={36} />
            {t('لا توجد إشعارات جديدة', 'No new notifications', 'Pas de nouvelles notifications')}
          </div>
        </SheetContent>
      </Sheet>

      {/* Saved placeholder */}
      <Sheet open={savedOpen} onOpenChange={setSavedOpen}>
        <SheetContent side={isRTL ? 'left' : 'right'} className={isRTL ? 'rtl' : 'ltr'}>
          <SheetHeader><SheetTitle className="text-[#0d47a1]">{t('المحفوظات', 'Saved', 'Favoris')}</SheetTitle></SheetHeader>
          <div className="py-10 text-center text-gray-500 text-sm">
            <Bookmark className="mx-auto text-gray-300 mb-2" size={36} />
            {t('لا توجد عناصر محفوظة', 'No saved items', 'Aucun élément enregistré')}
          </div>
        </SheetContent>
      </Sheet>

      {/* Payments placeholder */}
      <Sheet open={paymentsOpen} onOpenChange={setPaymentsOpen}>
        <SheetContent side={isRTL ? 'left' : 'right'} className={isRTL ? 'rtl' : 'ltr'}>
          <SheetHeader><SheetTitle className="text-[#0d47a1]">{t('طرق الدفع', 'Payment Methods', 'Moyens de paiement')}</SheetTitle></SheetHeader>
          <div className="py-6 text-center text-gray-600 text-sm space-y-4">
            <CreditCard className="mx-auto text-gray-300" size={36} />
            <p>
              {subscription
                ? t('لديك اشتراك نشط', 'You have an active subscription', "Vous avez un abonnement actif")
                : t('لا توجد طرق دفع محفوظة. أدر بطاقاتك من خلال صفحة الاشتراك.',
                    'No saved payment methods. Manage cards on the subscription page.',
                    'Aucun moyen enregistré. Gérez vos cartes via l\'abonnement.')}
            </p>
            <Link to="/subscriptions" onClick={() => setPaymentsOpen(false)}>
              <Button className="bg-[#1565c0] hover:bg-[#0d47a1]">{t('إدارة الاشتراك', 'Manage Subscription', 'Gérer')}</Button>
            </Link>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};
